// Restock Alerts (Predictive Restocking Engine) — what the platform would
// alert a seller about today, grouped by issue and by product. Detection
// only: sending (email, notification centre, Done / Snooze) arrives with the
// platform alerting system.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AuthContext from '../../../context/AuthContext';
import * as api from './services/restockAlertsApi';
import styles from './RestockAlerts.module.css';

const TRACKS = ['Replenishment', 'Fees & cost', 'Account health', 'Excess & capital',
  'Seasonal', 'Data & setup'];
const STATUS_LABEL = { due: 'Due today', active: 'Active', scheduled: 'Scheduled' };
const COVERAGE_LABEL = {
  live: 'Live', needs_threshold: 'Needs threshold', needs_data: 'Needs data',
  needs_history: 'Needs history', needs_delivery: 'Comes with notifications',
};
const SKU_PREVIEW = 8;

const fmtDate = (iso) => (iso
  ? new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
  : null);

const Tile = ({ label, value, accent }) => (
  <div className={`${styles.tile} ${accent ? styles[`tile_${accent}`] : ''}`}>
    <div className={styles.tileLabel}>{label}</div>
    <div className={styles.tileValue}>{value ?? '—'}</div>
  </div>
);

const Chip = ({ cls, children }) => <span className={`${styles.chip} ${cls}`}>{children}</span>;

const Detail = ({ detail }) => {
  if (!detail) return null;
  if (detail.reports) {
    return (
      <ul className={styles.detailList}>
        {detail.reports.map((r) => (
          <li key={r.report}>
            {r.report}: {r.last_updated ? `last updated ${fmtDate(r.last_updated)} (${r.age_days} days ago)` : 'never synced'}
          </li>
        ))}
      </ul>
    );
  }
  if (detail.measures) {
    const m = detail.measures;
    return (
      <div className={styles.detailList}>
        Sell-through {m.sell_through ?? '—'} · Excess share {m.excess_share ?? '—'} ·
        Stranded share {m.stranded_share ?? '—'} · In-stock rate {m.in_stock_rate ?? '—'}
        {' '}— {detail.excess_skus ?? 0} excess and {detail.stranded_skus ?? 0} stranded SKUs to clean up.
      </div>
    );
  }
  if (detail.missing_skus) {
    return <div className={styles.detailList}>Missing: {detail.missing_skus.join(', ')}</div>;
  }
  return null;
};

const AlertCard = ({ alert: a, onOpen }) => {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? a.skus : a.skus.slice(0, SKU_PREVIEW);
  return (
    <div className={`${styles.card} ${styles[`card_${a.status}`]}`}>
      <div className={styles.cardHead}>
        <span className={styles.alertId}>{a.id}</span>
        <span className={styles.alertName}>{a.name}</span>
        <Chip cls={styles[`prio_${a.priority}`]}>{a.priority}</Chip>
        <Chip cls={styles[`status_${a.status}`]}>{STATUS_LABEL[a.status]}</Chip>
        {a.new_count > 0 && <Chip cls={styles.chipNew}>{a.new_count} new</Chip>}
        {a.deadline && <span className={styles.deadline}>Deadline {fmtDate(a.deadline)}</span>}
      </div>
      <div className={styles.summary}>{a.summary}</div>
      <div className={styles.consequence}>{a.consequence}</div>
      {a.per_sku && (
        <table className={styles.skuTable}>
          <tbody>
            {shown.map((i) => (
              <tr key={i.sku}>
                <td className={styles.mono}>{i.sku}</td>
                <td className={styles.mono}>{i.asin || '—'}</td>
                <td>{i.consequence}</td>
                <td className={styles.right}>{i.new && <Chip cls={styles.chipNew}>new</Chip>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <Detail detail={a.detail} />
      <div className={styles.cardFoot}>
        <span className={styles.muted}>
          {a.per_sku && a.skus.length > SKU_PREVIEW && (
            <button className={styles.linkBtn} onClick={() => setExpanded(!expanded)}>
              {expanded ? 'Show fewer' : `Show all ${a.skus.length}`}
            </button>
          )}
          {a.sku_count > a.skus.length && ` Top ${a.skus.length} of ${a.sku_count} — open for the full list. `}
          {a.timing}{a.note ? ` · ${a.note}` : ''}
        </span>
        {a.route && <button className={styles.openBtn} onClick={() => onOpen(a.route)}>Open →</button>}
      </div>
    </div>
  );
};

const ProductRow = ({ product: p, onOpen }) => (
  <div className={styles.productRow}>
    <div className={styles.productHead}>
      <span className={styles.mono}>{p.asin || p.product}</span>
      <Chip cls={styles[`prio_${p.top_priority}`]}>{p.top_priority}</Chip>
      <span className={styles.muted}>
        {p.issue_count} issue{p.issue_count === 1 ? '' : 's'} · SKU {p.skus.join(', ')}
      </span>
    </div>
    <ul className={styles.issueList}>
      {p.issues.map((i, idx) => (
        <li key={`${i.id}-${i.sku}-${idx}`}>
          <span className={styles.alertId}>{i.id}</span> <b>{i.name}</b>
          {i.deadline && <span className={styles.deadline}> · {fmtDate(i.deadline)}</span>}
          {i.route && <button className={styles.linkBtn} onClick={() => onOpen(i.route)}>Open →</button>}
          <div className={styles.muted}>{i.consequence}</div>
        </li>
      ))}
    </ul>
  </div>
);

const RestockAlerts = () => {
  const authCtx = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get('view') === 'product' ? 'product' : 'issue';
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (refresh = false) => {
    if (!authCtx?.token) return;
    if (refresh) setRefreshing(true);
    try {
      const body = await api.getAlerts(authCtx.token, { refresh });
      setState({ loading: false, data: body.data, error: null });
    } catch (e) {
      setState((s) => ({ loading: false, data: s.data, error: e.message }));
    } finally {
      setRefreshing(false);
    }
  }, [authCtx?.token]);
  useEffect(() => { load(); }, [load]);

  const setView = (v) => {
    const next = new URLSearchParams(searchParams);
    next.set('view', v);
    setSearchParams(next);
  };

  const { loading, data, error } = state;
  if (loading && !data) {
    return (
      <div className={styles.page}>
        <div className={styles.muted}>
          Evaluating alerts — the first run of the day reads every inventory feature and can take up to a minute…
        </div>
      </div>
    );
  }
  if (error && !data) return <div className={styles.page}><div className={styles.error}>{error}</div></div>;
  if (!data) return null;

  const { counts, alerts, by_product: byProduct, coverage, assumptions } = data;
  const current = alerts.filter((a) => a.status !== 'scheduled');
  const upcoming = alerts.filter((a) => a.status === 'scheduled');
  const noLeadTime = !assumptions.lead_time_entered.FBA && !assumptions.lead_time_entered.MFN;
  const liveCount = coverage.filter((m) => m.status === 'live').length;

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div>
          <h2 className={styles.pageTitle}>Restock Alerts</h2>
          <p className={styles.pageSubtitle}>
            What the platform would alert you about today, read from Inventory Health,
            Restock and Inventory Performance. Alerts are detected here; sending them by
            email or notification, with Done / Snooze, comes with the platform alerting system.
          </p>
        </div>
        <button className={styles.refreshBtn} onClick={() => load(true)} disabled={refreshing}>
          {refreshing ? '↻ Refreshing…' : '↻ Refresh'}
        </button>
      </div>

      {error && <div className={`${styles.banner} ${styles.bannerWarn}`}>Refresh failed: {error}</div>}
      {noLeadTime && (
        <div className={`${styles.banner} ${styles.bannerWarn}`}>
          No lead time entered, so order-by and seasonal-peak alerts are off.{' '}
          <button className={styles.linkBtn}
                  onClick={() => navigate('/dashboard/seller-intelligence/inventory-health?tab=stock-out-risk')}>
            Set it in Inventory Health →
          </button>
        </div>
      )}
      {data.first_run && (
        <div className={`${styles.banner} ${styles.bannerInfo}`}>
          First evaluation for this account. Alerts that compare with yesterday — state worsened,
          inbound landed, now measurable, band stepped up — start from the next day.
        </div>
      )}

      <div className={styles.tileGrid}>
        <Tile label="Due today" value={counts.due_today} accent={counts.due_today ? 'critical' : 'ok'} />
        <Tile label="Active alerts" value={current.length} />
        <Tile label="Critical" value={counts.critical} accent={counts.critical ? 'critical' : null} />
        <Tile label="Products affected" value={counts.products} />
      </div>

      <div className={styles.assumptions}>
        <span>Seasonal peaks: <b>last season's spike ({assumptions.spike_multiple}× normal week)</b></span>
        <span>Reminder runway: <b>{assumptions.runway}</b></span>
        <span>Orders through: <b>{fmtDate(assumptions.orders_through) || '—'}</b></span>
        <span>As of <b>{fmtDate(data.as_of)}</b></span>
      </div>

      <div className={styles.viewToggle}>
        <button className={view === 'issue' ? styles.viewActive : ''} onClick={() => setView('issue')}>By issue</button>
        <button className={view === 'product' ? styles.viewActive : ''} onClick={() => setView('product')}>By product</button>
      </div>

      {view === 'issue' ? (
        <>
          {current.length === 0 && (
            <div className={`${styles.banner} ${styles.bannerOk}`}>Nothing needs attention today.</div>
          )}
          {TRACKS.map((track) => {
            const items = current.filter((a) => a.track === track);
            if (!items.length) return null;
            return (
              <section key={track} className={styles.trackSection}>
                <h3 className={styles.trackTitle}>{track}</h3>
                {items.map((a) => <AlertCard key={a.id} alert={a} onOpen={navigate} />)}
              </section>
            );
          })}
          {upcoming.length > 0 && (
            <section className={styles.trackSection}>
              <h3 className={styles.trackTitle}>Coming up</h3>
              {upcoming.map((a) => <AlertCard key={a.id} alert={a} onOpen={navigate} />)}
            </section>
          )}
        </>
      ) : (
        <section className={styles.trackSection}>
          {byProduct.length === 0
            ? <div className={`${styles.banner} ${styles.bannerOk}`}>No product-level issues today.</div>
            : byProduct.map((p) => <ProductRow key={p.product} product={p} onOpen={navigate} />)}
        </section>
      )}

      <details className={styles.coverage}>
        <summary>Notification matrix coverage — {liveCount} of {coverage.length} live</summary>
        <table className={styles.coverageTable}>
          <tbody>
            {coverage.map((m) => (
              <tr key={m.id}>
                <td className={styles.alertId}>{m.id}</td>
                <td>{m.name}</td>
                <td className={styles.muted}>{m.track}</td>
                <td><Chip cls={styles[`cov_${m.status}`]}>{COVERAGE_LABEL[m.status]}</Chip></td>
                <td className={styles.muted}>
                  {m.status_reason || (m.prior_day ? 'Compares with yesterday — starts on the second day' : '')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </div>
  );
};

export default RestockAlerts;
